import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { useResultHandler } from "../../../hooks/useResultHandler";
import { getNotificationList } from "../api/notification.api";
import type {
  NotificationFilter,
  NotificationItem,
  NotificationSortDirection,
  NotificationSortKey,
  NotificationStringFilterKey,
} from "../types/notification.types";

const EMPTY_FILTERS: NotificationFilter = {};
const ITEMS_PER_PAGE = 20;

export const useGetNotificationList = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationFilter>(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState<NotificationFilter>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<{
    key: NotificationSortKey | null;
    direction: NotificationSortDirection | null;
  }>({ key: "created_at", direction: "desc" });
  const { run: runLatestRequest, loading } = useLatestRequest();
  const { handleError } = useResultHandler();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const fetchNotifications = useCallback(async () => {
    try {
      setError(null);
      const response = await runLatestRequest((signal) =>
        getNotificationList(
          {
            keyword: debouncedFilters.keyword?.trim() || undefined,
            alert_type_id: debouncedFilters.alert_type_id,
            pin_status: debouncedFilters.pin_status,
            from: debouncedFilters.notification_date?.from,
            to: debouncedFilters.notification_date?.to,
            title: debouncedFilters.title,
            content: debouncedFilters.content,
            index: page - 1,
            size: ITEMS_PER_PAGE,
            sort_key: sorting.key ?? undefined,
            sort_direction: sorting.direction ?? undefined,
          },
          signal,
        ),
      );

      if (!response) return;
      setNotifications([...response.list]);
      setTotalCount(response.paging?.total ?? 0);
    } catch (fetchError) {
      setNotifications([]);
      setTotalCount(0);
      setError(handleError(fetchError, "Failed to load notifications"));
    }
  }, [debouncedFilters, handleError, page, runLatestRequest, sorting]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((current) => current.filter((notification) => notification.id !== id));
    setTotalCount((current) => Math.max(0, current - 1));
  }, []);

  const setNotificationPinned = useCallback((id: string, isPinned: boolean) => {
    setNotifications((current) =>
      current.map((notification) => ({
        ...notification,
        is_pinned: isPinned
          ? notification.id === id
          : notification.id === id
            ? false
            : notification.is_pinned,
      })),
    );
  }, []);

  const updateFilter = useCallback((key: NotificationStringFilterKey, value?: string) => {
    setFilters((current) => {
      if (!value) {
        const { [key]: _removed, ...remainingFilters } = current;
        return remainingFilters;
      }

      return { ...current, [key]: value };
    });
  }, []);

  const updateSorting = useCallback(
    (key: NotificationSortKey, direction: NotificationSortDirection | null) => {
      setSorting({ key: direction ? key : null, direction });
      setPage(1);
    },
    [],
  );

  return {
    notifications,
    loading,
    error,
    filters,
    setFilters,
    updateFilter,
    sorting,
    updateSorting,
    page,
    setPage,
    totalCount,
    itemsPerPage: ITEMS_PER_PAGE,
    refreshNotifications: fetchNotifications,
    removeNotification,
    setNotificationPinned,
  };
};
